import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, Redirect } from 'react-router-dom';
import { Button, TextField, Link, FormHelperText, Checkbox, Typography } from '@mui/material';
import { AppContext } from '@/AppContext';
import { convertErrors } from '../MeetupList/components/MeetupEditForm/MeetupEditForm';
import RegistrationCompleted from './components/RegistrationCompleted';
import ErrorBlock from '@/components/ErrorBlock/ErrorBlock';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { trackButtonClick } from '@/lib/tracking';

const PREFIX = 'Registration';

const classes = {
  root: `${PREFIX}-root`,
  grid: `${PREFIX}-grid`,
  quoteContainer: `${PREFIX}-quoteContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  contentHeader: `${PREFIX}-contentHeader`,
  logoImage: `${PREFIX}-logoImage`,
  contentBody: `${PREFIX}-contentBody`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  textField: `${PREFIX}-textField`,
  formContainer: `${PREFIX}-formContainer`,
  bottomLink: `${PREFIX}-bottomLink`,
  singIn: `${PREFIX}-singIn`,
  policy: `${PREFIX}-policy`,
  policyCheckbox: `${PREFIX}-policyCheckbox`,
  signUpButton: `${PREFIX}-signUpButton`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    height: '100%',
  },

  [`& .${classes.grid}`]: {
    height: '100%',
  },

  [`& .${classes.quoteContainer}`]: {
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },

  [`& .${classes.contentContainer}`]: {},

  [`& .${classes.content}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.contentHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.logoImage}`]: {
    marginLeft: theme.spacing(4),
  },

  [`& .${classes.contentBody}`]: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.form}`]: {
    height: '592px',
    width: '600px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '30px',
    // flexBasis: 700,
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },

  [`& .${classes.title}`]: {
    textAlign: 'center',
    // marginTop: theme.spacing(3),
    paddingBottom: '20px',
  },

  [`& .${classes.textField}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.formContainer}`]: {
    height: 'calc(100% - 100px)',
    padding: '20px  45px',
    border: '1px solid #ffffff',
    borderBottomColor: '#DEDEDE',
    borderTopColor: '#DEDEDE',
  },

  [`& .${classes.bottomLink}`]: {
    marginTop: '10px',
    width: '100%',
    textAlign: 'center',
  },

  [`& .${classes.singIn}`]: {
    textAlign: 'center',
    marginTop: '20px',
  },

  [`& .${classes.policy}`]: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.policyCheckbox}`]: {
    marginLeft: '-14px',
  },

  [`& .${classes.signUpButton}`]: {
    margin: theme.spacing(2, 0),
  },
}));

export {};

const Registration = props => {
  const appContext = useContext(AppContext);
  const [inputType, setInputType] = useState('password');

  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  useEffect(() => {
    const validateForm = values => {
      const errors = {};

      if (!values.policy) {
        errors.policy = 'Поле обязательное';
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };

    const errors = validateForm(formState.values);

    setFormState(formState => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {},
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (errors) {
      setFormState({
        ...formState,
        ...convertErrors(errors),
      });
    }
  }, [errors]);

  const handleChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleRegister = event => {
    event.preventDefault();
    if (!formState.values.policy) {
      setFormState(formState => ({
        ...formState,
        errors: {
          policy: 'Поле является обязательным',
        },
        touched: {
          ...formState.touched,
          policy: true,
        },
      }));
      return null;
    }
    trackButtonClick({ button: 'register', location: 'registration' });
    appContext.accountApi
      .registerAccount(formState.values)
      .then(({ data }) => {
        appContext.history.push('/registration-success');
      })
      .catch(error => {
        const data = error.response.data;
        setErrors(data.errors || [data]);
      });
  };

  const showPassword = () => {
    if (inputType === 'password') {
      setInputType('text');
    } else {
      setInputType('password');
    }
  };

  const hasError = field => (formState.touched[field] && formState.errors[field] ? true : false);

  return (
    <Root className={classes.root}>
      <div className={classes.content}>
        {/*<div className={classes.contentHeader}></div>*/}
        <div className={classes.contentBody}>
          <ErrorBlock message={formState.errors.token} />
          <form className={classes.form} onSubmit={handleRegister}>
            <Typography className={classes.title} variant='h2'>
              Регистрация аккаунта
            </Typography>
            <div className={classes.formContainer}>
              <TextField
                className={classes.textField}
                error={hasError('accountName')}
                fullWidth
                helperText={hasError('accountName') ? formState.errors.accountName : null}
                label='Название организации'
                name='accountName'
                onChange={handleChange}
                type='text'
                value={formState.values.accountName || ''}
                variant='outlined'
                required={false}
                autoComplete={'off'}
              />
              <TextField
                className={classes.textField}
                error={hasError('ownerEmail')}
                fullWidth
                helperText={hasError('ownerEmail') ? formState.errors.ownerEmail : null}
                label='Адрес электронной почты'
                name='ownerEmail'
                onChange={handleChange}
                type='text'
                value={formState.values.ownerEmail || ''}
                variant='outlined'
                required={false}
                autoComplete={'off'}
              />
              <TextField
                className={classes.textField}
                error={hasError('ownerDisplayName')}
                fullWidth
                helperText={hasError('ownerDisplayName') ? formState.errors.ownerDisplayName : null}
                label='Имя пользователя'
                name='ownerDisplayName'
                onChange={handleChange}
                type='text'
                value={formState.values.ownerDisplayName || ''}
                variant='outlined'
                required={false}
                autoComplete={'off'}
              />
              <TextField
                className={classes.textField}
                error={hasError('ownerPassword')}
                fullWidth
                helperText={hasError('ownerPassword') ? formState.errors.ownerPassword : null}
                label='Пароль'
                name='ownerPassword'
                onChange={handleChange}
                type={inputType}
                value={formState.values.ownerPassword}
                variant='outlined'
                required={false}
                autoComplete='new-password'
              />
              <span style={{ position: 'relative' }}>
                {inputType === 'password' ? (
                  <VisibilityOff
                    onClick={showPassword}
                    style={{ position: 'absolute', top: 32, right: 10, cursor: 'pointer' }}
                  />
                ) : (
                  <Visibility
                    onClick={showPassword}
                    style={{ position: 'absolute', top: 32, right: 10, cursor: 'pointer' }}
                  />
                )}
              </span>
              <div className={classes.policy}>
                <Checkbox
                  checked={formState.values.policy || false}
                  className={classes.policyCheckbox}
                  color='primary'
                  name='policy'
                  onChange={handleChange}
                  // required={true}
                />
                <Typography className={classes.policyText} color='textSecondary' variant='body1'>
                  Я соглашаюсь с&nbsp;
                  <Link
                    color='primary'
                    component={RouterLink}
                    to='/docs/terms-of-service.pdf'
                    underline='always'
                    variant='h6'
                    target={'_blank'}
                  >
                    Условиями использования продукта
                  </Link>
                  &nbsp;и принимаю&nbsp;
                  <Link
                    color='primary'
                    href='/privacy-policy.pdf'
                    underline='always'
                    variant='h6'
                    target={'_blank'}
                  >
                    Политику конфиденциальности
                  </Link>
                  .
                </Typography>
              </div>
              {hasError('policy') && <FormHelperText error>{formState.errors.policy}</FormHelperText>}
              <Button
                className={classes.signUpButton}
                color='primary'
                fullWidth
                size='large'
                type='submit'
                variant='contained'
              >
                Зарегистрироваться
              </Button>
            </div>
            <div className={classes.singIn}>
              <Typography variant='body1' component='span' sx={{ opacity: 0.5 }}>
                Уже есть аккаунт?
              </Typography>
              <Button
                variant='text'
                color='primary'
                onClick={() => appContext.history.push('/sign-in')}
                sx={{ ml: 1 }}
              >
                Войти
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Root>
  );
};
// Legacy hook for backward compatibility
export const useStyles = () => classes;

export default Registration;
